import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "../common/button";

// "Copy to": pick the companies that should get one company's document settings
interface CopySettingsDialogProps {
  open: boolean;
  source: string; // the company copied from
  companies: string[]; // all the customer's Priority companies
  onClose: () => void;
  onCopy: (targets: string[]) => void;
}

export const CopySettingsDialog: React.FC<CopySettingsDialogProps> = ({
  open,
  source,
  companies,
  onClose,
  onCopy,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const targets = companies.filter((name) => name !== source);
  const allSelected = targets.length > 0 && selected.length === targets.length;

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const toggle = (name: string) =>
    setSelected((current) =>
      current.includes(name) ? current.filter((n) => n !== name) : [...current, name]
    );

  return (
    // Rendered inside ClientDialog, so its rtlCache flips these styles too; the portal needs its own dir
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle>
        {t("COPY_SETTINGS_TITLE") || "Copy document settings"}{" "}
        <span dir="ltr">({source})</span>
      </DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              onChange={() => setSelected(allSelected ? [] : targets)}
            />
          }
          label={t("SELECT_ALL_COMPANIES") || "All companies"}
        />
        {/* Indented under "all companies" - written for LTR, flipped by rtlCache */}
        <FormGroup sx={{ pl: 3 }}>
          {targets.map((name) => (
            <FormControlLabel
              key={name}
              control={<Checkbox checked={selected.includes(name)} onChange={() => toggle(name)} />}
              label={<span dir="ltr">{name}</span>}
            />
          ))}
        </FormGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("COPY_SETTINGS_HINT") ||
            "Replaces the selected companies' settings. Nothing is saved until you click Update."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button label={t("CANCEL")} onClick={onClose} />
        <Button
          label={t("COPY_SETTINGS") || "Copy"}
          onClick={() => onCopy(selected)}
          disabled={selected.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};
